import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { site } from '@/data/site';
import { sections } from '@/data/sections';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { LOADER_SESSION_KEY } from '@/lib/constants';

const COUNT_MS = 1300;
const LIFT_MS = 700;

type Phase = 'counting' | 'lifting' | 'gone';

interface LoaderProps {
  /** Fired once the curtain is clear — the hero starts its own sequence here. */
  readonly onDone: () => void;
}

/**
 * The load sequence — mono counter, acid rule, curtain lift.
 *
 * v6 had no loader at all, which is part of why it read as a page rather than
 * an authored piece (CLAUDE.md §0). This is the first thing a visitor sees, so
 * it has three hard rules: it is skippable on any input, it runs at most once
 * per session, and it does not exist at all under reduced motion.
 *
 * `sessionStorage` is wrapped in try/catch — it throws outright in some
 * privacy modes, and a decorative loader must never be the thing that breaks
 * the site.
 */

/** Session flag read defensively — `sessionStorage` throws outright in some privacy modes. */
function seenThisSession(): boolean {
  try {
    return sessionStorage.getItem(LOADER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function Loader({ onDone }: LoaderProps) {
  const reduced = useReducedMotion();
  // Decided in the initializer, not an effect: skipping has to be true on the
  // very first render, or the curtain paints for a frame before disappearing.
  // It also keeps this off React's cascading-render path (react-hooks/set-state-in-effect).
  const [phase, setPhase] = useState<Phase>(() =>
    reduced || seenThisSession() ? 'gone' : 'counting',
  );
  const [count, setCount] = useState(0);
  // The effect below drives `phase` through 'lifting' → 'gone'. Depending on
  // `phase` directly would therefore re-run it and restart the count, so the
  // mount-time decision is frozen in a ref and the effect runs exactly once.
  const skipRef = useRef(phase === 'gone');

  useEffect(() => {
    if (skipRef.current) {
      onDone();
      return;
    }

    try {
      sessionStorage.setItem(LOADER_SESSION_KEY, '1');
    } catch {
      /* ignore */
    }

    let raf = 0;
    let liftTimer = 0;
    let finished = false;
    const start = performance.now();

    const finish = () => {
      if (finished) return;
      finished = true;
      cancelAnimationFrame(raf);
      setCount(100);
      setPhase('lifting');
      liftTimer = window.setTimeout(() => {
        setPhase('gone');
        onDone();
      }, LIFT_MS);
    };

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / COUNT_MS);
      // Cubic ease-out so the number decelerates into 100 rather than
      // hitting it at full speed — reads as settling, not stopping.
      setCount(Math.round((1 - Math.pow(1 - t, 3)) * 100));
      if (t < 1) raf = requestAnimationFrame(tick);
      else finish();
    };
    raf = requestAnimationFrame(tick);

    window.addEventListener('keydown', finish);
    window.addEventListener('pointerdown', finish);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(liftTimer);
      window.removeEventListener('keydown', finish);
      window.removeEventListener('pointerdown', finish);
    };
  }, [onDone]);

  if (phase === 'gone') return null;

  // Rendered once and reused by both halves of the veil so the two copies
  // can never drift out of sync with each other.
  const content = (
    <div className="flex h-svh flex-col justify-between gap-8 p-(--gutter)">
      <p className="label">{site.name}</p>

      {/* The manifest. An empty curtain with a number in the corner is exactly
          the emptiness v6 was rejected for, so the wait does real work: the
          eight sections print in as the count climbs, from `sections.ts`, so
          a renamed or reordered section can never disagree with the nav. */}
      <ul className="space-y-1">
        {sections.map((s, i) => (
          <li
            key={s.id}
            className={cn(
              'label flex items-center gap-3 transition-opacity duration-300',
              count >= ((i + 1) / sections.length) * 100 ? 'opacity-100' : 'opacity-0',
            )}
          >
            <span className="text-acid tabular-nums">{s.number}</span>
            <span className="text-bone">{s.label}</span>
          </li>
        ))}
      </ul>

      <div>
        {/* Progress rule — the seal's first appearance on the site. */}
        <div className="bg-edge relative h-0.5 w-full">
          <div
            className="bg-acid absolute inset-y-0 left-0 origin-left"
            style={{ width: `${count}%` }}
          />
        </div>
        <div className="mt-4 flex items-baseline justify-between">
          <p className="label">Loading</p>
          {/* v9: the count is set in Fraunces at display scale — a ledger
              numeral, not terminal telemetry. `tabular-nums` engages
              Fraunces' `tnum` feature so digits don't reflow as it climbs
              (it is right-aligned; proportional figures would visibly
              shuffle its left edge every frame). */}
          <p className="text-acid display [font-variant-numeric:tabular-nums] text-[clamp(3rem,12vw,8rem)] leading-none">
            {String(count).padStart(3, '0')}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div aria-hidden="true" className="fixed inset-0 z-100">
      {/* Split veil: one composition, cut in half. Each half is a clipped
          window onto an identical full-height copy of `content` — the top
          window aligned to its own top edge (showing rows 0–50vh), the
          bottom window aligned to its own bottom edge (showing rows
          50–100vh) — so together they reconstruct the exact same page a
          single panel would have shown. Cutting it this way, rather than
          splitting the content itself into two groups, is what lets both
          halves slide apart like doors without anything inside visibly
          jumping between them. */}
      <div
        className={cn(
          'bg-void ease-exit absolute inset-x-0 top-0 h-1/2 overflow-hidden transition-transform duration-700',
          phase === 'lifting' && '-translate-y-full',
        )}
      >
        <div className="absolute inset-x-0 top-0">{content}</div>
      </div>
      <div
        className={cn(
          'bg-void ease-exit absolute inset-x-0 bottom-0 h-1/2 overflow-hidden transition-transform duration-700',
          phase === 'lifting' && 'translate-y-full',
        )}
      >
        <div className="absolute inset-x-0 bottom-0">{content}</div>
      </div>
    </div>
  );
}
